import React, {useRef, useState} from "react";
import {FaPrint, FaFilePdf, FaFileWord, FaFileExcel, FaEllipsisV, FaWindowRestore} from "react-icons/fa";
import * as XLSX from "xlsx";import DraggableTearsheetWrapper from "./DraggableTearsheetWrapper";
import VendorTearsheet from "./VendorTearsheet";
import ProductTearsheet from "./ProductTearsheet";
import axios from "axios";
import { DraggableModalBox } from "./DraggableModalBox";

export default function TearsheetDock({
                                          openTearSheets, zOrder,
                                          openSheet, closeSheet, minimizeSheet, restoreSheet, focusSheet,
                                          contextPath,
                                          tabWidth = 180, tabGap = 5,  highlight, mode = "profiler"
                                      }) {
    const normalizeProductIds = (input) => {
        // Accept number, string "1,2,3", or array
        const parts = Array.isArray(input)
            ? input.map(String)
            : typeof input === "number"
                ? [String(input)]
                : String(input).split(/[,\s]+/);

        // Trim, keep digits only, de-dupe, keep order
        const seen = new Set();
        const out = [];
        for (const raw of parts) {
            const s = String(raw).trim();
            if (!s) continue;
            if (!/^\d+$/.test(s)) continue; // numeric IDs only
            if (!seen.has(s)) {
                seen.add(s);
                out.push(s);
            }
        }
        return out.join(",");
    };
    const findGlossaryTarget = (start) => {
        let node = start instanceof Element ? start : start?.parentElement;
        while (node) {
            if (node.dataset?.term) return node;
            if (node.classList?.contains("glossary-term")) return node;

            const style = (node.getAttribute?.("style") || "").replace(/\s+/g, "").toLowerCase();
            if (style.includes("color:#990026;cursor:pointer;") || style.includes("color:#ee7600;cursor:pointer;")) {
                return node;
            }

            node = node.parentElement;
        }
        return null;
    };

    const handleGlossaryClick = async (e) => {
        const target = findGlossaryTarget(e.target);
        if (!target) return;

        const term = (target.dataset.term || target.innerText || "").trim();
        if (!term) return;
        const token = localStorage.getItem("token");
        const glossaryBase = contextPath || "";

        try {
            const response = await axios.get(
                `${glossaryBase}/glossary/definition/${encodeURIComponent(term)}`,
                {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    responseType: "text",
                }
            );

            if (response.data) {
                const existing = document.getElementById("glossary-popup");
                if (existing) existing.remove();

                const popup = document.createElement("div");
                popup.id = "glossary-popup";
                const isAltDataMode = String(mode || "").trim().toLowerCase() === "altdata";
                const accentColor = isAltDataMode ? "#990026" : "#EE7600";
                const accentGlow = isAltDataMode ? "rgba(153, 0, 38, 0.18)" : "rgba(238, 118, 0, 0.18)";
                Object.assign(popup.style, {
                    position: "absolute",
                    top: `${e.pageY + 14}px`,
                    left: `${e.pageX}px`,
                    zIndex: "10001",
                    width: "min(360px, calc(100vw - 32px))",
                    maxWidth: "360px",
                    padding: "14px 16px 16px",
                    borderRadius: "14px",
                    border: "1px solid rgba(15, 23, 42, 0.12)",
                    background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
                    boxShadow: "0 18px 45px rgba(15, 23, 42, 0.18)",
                    color: "#0f172a",
                    fontSize: "13px",
                    lineHeight: "1.55",
                    fontFamily: "\"Segoe UI\", sans-serif",
                    backdropFilter: "blur(10px)"
                });

                popup.innerHTML = `
                    <div style="display:flex;align-items:center;gap:10px;margin-bottom:10px;">
                        <span style="display:inline-block;width:10px;height:10px;border-radius:999px;background:${accentColor};box-shadow:0 0 0 4px ${accentGlow};"></span>
                        <div style="min-width:0;">
                            <div style="font-size:11px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#64748b;">Glossary</div>
                            <div style="font-size:15px;font-weight:700;color:#0f172a;word-break:break-word;">${term}</div>
                        </div>
                    </div>
                    <div style="height:1px;background:linear-gradient(90deg, ${accentColor}, rgba(148, 163, 184, 0.28));margin-bottom:12px;"></div>
                    <div style="color:#334155;word-break:break-word;">${response.data}</div>
                `;
                document.body.appendChild(popup);

                const removePopup = () => {
                    if (popup) popup.remove();
                    window.removeEventListener("scroll", removePopup, true);
                    document.removeEventListener("click", removePopup);
                };

                window.addEventListener("scroll", removePopup, true);
                setTimeout(() => document.addEventListener("click", removePopup), 10);
            }
        } catch (err) {
            console.error("Glossary fetch failed:", err);
        }
    };

    const splitTwoWordsPerLine = (name = "") => {
        const words = String(name).trim().split(/\s+/).filter(Boolean);
        const lines = [];
        for (let i = 0; i < words.length; i += 2) {
            lines.push(words.slice(i, i + 2).join(" "));
        }
        return lines.length ? lines : [""];
    };

    const escapeHtml = (s = "") =>
        String(s)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");

    const formatTwoWordsPerLineHtml = (name = "") =>
        splitTwoWordsPerLine(name).map(escapeHtml).join("<br/>");

    const buildAllTabsWorkbook = (which) => {
        const modal = which === "data" ? dataModal : functionalityModal;
        const headerCol = which === "data" ? "Data Items" : "Functionality Items";

        const wb = XLSX.utils.book_new();

        [1, 2, 3].forEach((tab) => {
            const fctDto = modal.content?.[tab] || {};
            const { products = [], rows = [], productFeaturesMap = {} } = fctDto;
            if (!products.length || !rows.length) return;

            const aoa = [];
            aoa.push([headerCol, ...products.map((p) => (p?.name ? String(p.name) : ""))]);

            rows.forEach((r) => {
                const label = r?.label ? String(r.label) : "";

                if (r?.mainHeader) {
                    aoa.push([label, ...products.map(() => "")]);
                    return;
                }

                aoa.push([
                    label,
                    ...products.map((p) => {
                        const feats = productFeaturesMap?.[p.id] || [];
                        const has = Array.isArray(feats) && feats.includes(r.featureId);
                        return has ? "✓" : "✗";
                    }),
                ]);
            });

            const ws = XLSX.utils.aoa_to_sheet(aoa);
            ws["!cols"] = [{ wch: 45 }, ...products.map(() => ({ wch: 22 }))];

            XLSX.utils.book_append_sheet(wb, ws, TAB_LABELS[tab] || `Tab ${tab}`);
        });

        return wb;
    };

    const handleExportAllTabsExcel = (which) => {
        try {
            const wb = buildAllTabsWorkbook(which);

            if (!wb.SheetNames?.length) {
                alert("Nothing to export.");
                return;
            }

            const baseFilename = which === "data" ? "fct-data" : "fct-functionality";
            const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });

            const blob = new Blob([out], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            downloadBlob(blob, `${baseFilename}.xlsx`);
        } catch (err) {
            console.error("Excel export failed", err);
            alert("Sorry, Excel export failed.");
        }
    };

    const [dataModal, setDataModal] = useState({
        visible: false,
        productIds: null,
        activeTab: 1,
        content: { 1: {}, 2: {}, 3: {} },
        loading: false,
    });

    const [functionalityModal, setFunctionalityModal] = useState({
        visible: false,
        productIds: null,
        activeTab: 1,
        content: { 1: {}, 2: {}, 3: {} },
        loading: false,
    });

    const [showDataExportMenu, setShowDataExportMenu] = useState(false);
    const [showFuncExportMenu, setShowFuncExportMenu] = useState(false);
    const [dataModalZIndex, setDataModalZIndex] = useState(2000);
    const [functionalityModalZIndex, setFunctionalityModalZIndex] = useState(2000);

    const RustyTick = () => (
        <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            style={{ color: "var(--rusty-orange, #e87722)" }}
            xmlns="http://www.w3.org/2000/svg"
        >
            <path d="M4 9.5l3.5 3.5L14 6" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    const RustyCross = () => (
        <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            style={{ color: "var(--threedi-blue, #25408f)" }}
            xmlns="http://www.w3.org/2000/svg"
        >
            <path d="M5 5l8 8M13 5l-8 8" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" />
        </svg>
    );

    React.useEffect(() => {
        const handler = (e) => {
            openDataModalForProduct(e.detail);
        };
        window.addEventListener("open-profiler-data-modal", handler);
        return () => window.removeEventListener("open-profiler-data-modal", handler);
    }, []);


    /** --- Openers (accept multiple product IDs) --- */
    const openDataModalForProduct = (productIds) => {
        const token = localStorage.getItem("token");
        const ids = normalizeProductIds(productIds);
        if (!ids) return;

        setDataModalZIndex(2000); // Reset to high z-index when opening
        setDataModal((dm) => ({
            ...dm,
            visible: true,
            productIds: ids,
            loading: true,
            activeTab: 1,
            content: { 1: {}, 2: {}, 3: {} },
        }));

        axios
            .get(`${process.env.REACT_APP_API_URL}/data-fct`, {
                params: { multiProductIds: ids, mode: "profiler" },
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            })
            .then(({ data }) => setDataModal((dm) => ({ ...dm, content: data, loading: false })))
            .catch((err) => {
                console.error(err);
                setDataModal((dm) => ({ ...dm, loading: false }));
            });
    };

    const openFunctionalityModalForProduct = (productIds) => {
        const token = localStorage.getItem("token");
        const ids = normalizeProductIds(productIds);
        if (!ids) return;

        setFunctionalityModalZIndex(2000); // Reset to high z-index when opening
        setFunctionalityModal((dm) => ({
            ...dm,
            visible: true,
            productIds: ids,
            loading: true,
            activeTab: 1,
            content: { 1: {}, 2: {}, 3: {} },
        }));

        axios
            .get(`${process.env.REACT_APP_API_URL}/functionality-fct`, {
                params: { multiProductIds: ids, mode: "profiler" },
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            })
            .then(({ data }) => setFunctionalityModal((dm) => ({ ...dm, content: data, loading: false })))
            .catch((err) => {
                console.error(err);
                setFunctionalityModal((dm) => ({ ...dm, loading: false }));
            });
    };

    const TAB_LABELS = {
        1: "Fixed Income",
        2: "Equity",
        3: "Commodity",
    };

    const buildAllTabsHtml = (which) => {
        const modal = which === "data" ? dataModal : functionalityModal;
        const title =
            which === "data"
                ? "Data & Coverage – Data Items"
                : "Data & Coverage – Functionality Items";
        const headerCol =
            which === "data" ? "Data Items" : "Functionality Items";

        const productCount = modal.productIds
            ? modal.productIds.split(",").filter(Boolean).length
            : 0;

        let html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${title}</title>
  <style>
    body { font-family: 'DejaVu Sans', sans-serif; font-size: 12px; color: #000; }
    td, th { font-family: 'DejaVu Sans', sans-serif; }
    h1 { font-size: 18px; margin-bottom: 4px; }
    h2 { font-size: 15px; margin-top: 16px; margin-bottom: 4px; }
    table { border-collapse: collapse; width: 100%; margin-top: 4px; }
    th, td { border: 1px solid #ccc; padding: 4px 6px; vertical-align: middle; }
    th { background: #25408f; color: #fff; text-align: center; }
    tr.main-header td { background: #e5e5e5; font-weight: 600; }
    .meta { font-size: 11px; margin-bottom: 10px; color: #555; }
  </style>
</head>
<body>
  <h1>${title}</h1>
`;


        [1, 2, 3].forEach((tab) => {
            const fctDto = modal.content?.[tab] || {};
            const { products = [], rows = [], productFeaturesMap = {} } = fctDto;
            if (!products.length || !rows.length) return;

            html += `<h2>${TAB_LABELS[tab] || `Tab ${tab}`}</h2>`;
            html += `<table><thead><tr>`;
            html += `<th style="text-align:left;">${headerCol}</th>`;
            products.forEach((p) => {
                const nameHtml = formatTwoWordsPerLineHtml((p && p.name) || "");
                html += `<th><div style="white-space:normal; line-height:1.15;">${nameHtml}</div></th>`;
            });
            html += `</tr></thead><tbody>`;

            rows.forEach((r) => {
                const isMainHeader = !!r.mainHeader;
                html += `<tr class="${isMainHeader ? "main-header" : ""}">`;
                html += `<td>${r.label || ""}</td>`;

                products.forEach((p) => {
                    const features = productFeaturesMap[p.id] || [];
                    const has = features.includes(r.featureId);
                    if (isMainHeader) {
                        html += `<td></td>`;
                    } else {
                        const symbol = has ? "&#10003;" : "&#10007;"; // ✓ / ✗
                        html += `<td style="text-align:center;font-size:14px;">${symbol}</td>`;
                    }
                });

                html += `</tr>`;
            });

            html += `</tbody></table>`;
        });

        html += `
</body>
</html>`;

        return html;
    };

    const handlePrintAllTabs = (which) => {
        const html = buildAllTabsHtml(which);

        const iframe = document.createElement("iframe");
        iframe.style.position = "fixed";
        iframe.style.right = "0";
        iframe.style.bottom = "0";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "0";
        document.body.appendChild(iframe);

        const frameWindow = iframe.contentWindow || iframe.contentDocument;
        const frameDoc = frameWindow.document || iframe.contentDocument;

        frameDoc.open();
        frameDoc.write(html);
        frameDoc.close();

        setTimeout(() => {
            frameWindow.focus();
            frameWindow.print();

            setTimeout(() => {
                document.body.removeChild(iframe);
            }, 1000);
        }, 100);
    };


    const downloadBlob = (blob, fileName) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    };


    const handleExportAllTabsPdf = async (which) => {
        try {
            const html = buildAllTabsHtml(which);
            const token = localStorage.getItem("token");

            const scope = which === "data" ? "data" : "functionality";
            const baseFilename = scope === "data" ? "fct-data" : "fct-functionality";

            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/exports/fct`,
                {
                    html,
                    scope,
                    filename: baseFilename,
                },
                {
                    params: { format: "pdf" },
                    headers: {
                        "Content-Type": "application/json",
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    responseType: "blob",
                }
            );

            downloadBlob(res.data, `${baseFilename}.pdf`);
        } catch (err) {
            console.error("PDF export failed", err);
            alert("Sorry, PDF export failed.");
        }
    };

    const handleExportAllTabsWord = async (which) => {
        try {
            const html = buildAllTabsHtml(which);
            const token = localStorage.getItem("token");

            const scope = which === "data" ? "data" : "functionality";
            const baseFilename = scope === "data" ? "fct-data" : "fct-functionality";

            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/exports/fct`,
                {
                    html,
                    scope,
                    filename: baseFilename,
                },
                {
                    params: { format: "docx" },
                    headers: {
                        "Content-Type": "application/json",
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    responseType: "blob",
                }
            );

            downloadBlob(res.data, `${baseFilename}.docx`);
        } catch (err) {
            console.error("Word export failed", err);
            alert("Sorry, Word export failed.");
        }
    };

    return (
        <>
            {/* Minimized tabs */}
            {openTearSheets.filter((s) => s.minimized).map((sheet, idx) => (
                <div
                    key={`${sheet.type}-${sheet.id}-minibar`}
                    className="fixed bottom-4 z-[120] bg-blue-900 text-white rounded-2xl px-3 py-2 flex items-center shadow-xl cursor-pointer"
                    style={{ width: tabWidth, left: 4 + idx * (tabWidth + tabGap), zIndex: 2000 + idx }}
                    onClick={(e) => {
                        if (e.target.classList.contains("minibar-close")) return;
                        restoreSheet(sheet.id, sheet.type);
                    }}
                >
                    <FaWindowRestore className="mr-2" />
                    <span className="truncate flex-1">{sheet.title}</span>
                    <button
                        className="ml-2 minibar-close hover:text-red-400"
                        style={{ background: "transparent", border: 0, outline: 0, cursor: "pointer", fontSize: "1.25rem" }}
                        onClick={(e) => {
                            e.stopPropagation();
                            closeSheet(sheet.id, sheet.type);
                        }}
                        title="Close"
                    >
                        ×
                    </button>
                </div>
            ))}

            {/* Draggable popups */}
            {openTearSheets.map((sheet, idx) => (
                <DraggableTearsheetWrapper
                    key={`${sheet.type}-${sheet.id}`}
                    id={`${sheet.type}-${sheet.id}`}
                    title={sheet.title}
                    minimized={sheet.minimized}
                    zIndex={1000 + zOrder.indexOf(`${sheet.type}-${sheet.id}`)}
                    onMinimize={() => minimizeSheet(sheet.id, sheet.type)}
                    onRestore={() => restoreSheet(sheet.id, sheet.type)}
                    onClose={() => closeSheet(sheet.id, sheet.type)}
                    onClick={() => focusSheet(sheet.id, sheet.type)}
                    initialPos={{ x: 200 + 30 * idx, y: 120 + 30 * idx }}
                >
                    {sheet.type === "vendor" ? (
                        <VendorTearsheet vendorId={sheet.id} contextPath={contextPath}  onOpenProductPopup={(id, name) => openSheet("product", id, name)} highlight={highlight} mode={mode}  onGlossaryClick={handleGlossaryClick}/>
                    ) : (
                        <ProductTearsheet
                            productId={sheet.id}
                            contextPath={contextPath}
                            onOpenProductPopup={(id, name) => openSheet("product", id, name)}
                            onOpenVendorPopup={(id, name) => openSheet("vendor", id, name)}
                            onOpenDataModal={openDataModalForProduct}
                            onOpenFunctionalityModal={openFunctionalityModalForProduct}
                            highlight={highlight}
                            mode={mode}
                            onGlossaryClick={handleGlossaryClick}
                        />
                    )}
                </DraggableTearsheetWrapper>
            ))}

            {/* FCT Data Popup (supports MANY products) */}
            {dataModal.visible && (
                <div
                    className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
                    style={{ zIndex: dataModalZIndex }}
                    onClick={(e) => {
                        // If clicking on the backdrop (not the modal content), lower z-index
                        if (e.target === e.currentTarget) {
                            setDataModalZIndex(999);
                        }
                    }}
                >
                    <DraggableModalBox
                        onClose={() => {
                            setShowDataExportMenu(false);
                            setDataModal((dm) => ({ ...dm, visible: false }));
                        }}
                        onFocus={() => setDataModalZIndex(2000)}
                    >
                        <div className="mb-4">
                            {/* Close row */}
                            <div className="flex justify-end mb-2">
                                <button
                                    className="text-2xl font-bold leading-none px-2 hover:text-red-500"
                                    type="button"
                                    onClick={() => {
                                        setShowDataExportMenu(false);
                                        setDataModal((dm) => ({ ...dm, visible: false }));
                                    }}
                                >
                                    ×
                                </button>
                            </div>

                            {/* Tabs + product count + 3-dots */}
                            <div className="flex items-center justify-between gap-4">
                                <div className="flex border-b border-gray-200 flex-shrink-0">
                                    {[1, 2, 3].map((tab) => {
                                        const label = tab === 1 ? "Fixed Income" : tab === 2 ? "Equity" : "Commodity";
                                        const isActive = dataModal.activeTab === tab;
                                        const accentBg = { backgroundColor: "var(--rusty-orange, #e87722)", color: "#fff" };

                                        return (
                                        <button
                                            key={tab}
                                            onClick={() => setDataModal((dm) => ({ ...dm, activeTab: tab }))}
                                            className={`px-4 py-2 rounded border -mb-px transition ${
                                                isActive ? "" : "border-transparent text-gray-500 hover:text-gray-700"
                                            }`}
                                            style={isActive ? accentBg : undefined}
                                        >
                                            {label}
                                        </button>

                                    );
                                    })}
                                </div>

                                <div className="flex items-center gap-3 text-sm text-gray-600 ml-auto">
            <span>
              {(() => {
                  const dto = dataModal.content[dataModal.activeTab] || {};
                  const count = (dto.products || []).length;
                  return count ? `${count} product${count !== 1 ? "s" : ""}` : "";
              })()}
            </span>

                                    <div className="relative">
                                        <button
                                            type="button"
                                            className="p-1 rounded hover:bg-gray-100"
                                            title="More options"
                                            onClick={() => setShowDataExportMenu((prev) => !prev)}
                                        >
                                            <FaEllipsisV />
                                        </button>

                                        {showDataExportMenu && (
                                            <div className="absolute right-0 mt-2 w-44 rounded-md border bg-white shadow-lg z-20 text-sm">
                                                <button
                                                    type="button"
                                                    className="flex w-full items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left"
                                                    onClick={() => {
                                                        setShowDataExportMenu(false);
                                                        handlePrintAllTabs("data");
                                                    }}
                                                >
                                                    <FaPrint />
                                                    <span>Print all tabs</span>
                                                </button>

                                                <button
                                                    type="button"
                                                    className="flex w-full items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left"
                                                    onClick={() => {
                                                        setShowDataExportMenu(false);
                                                        handleExportAllTabsPdf("data");
                                                    }}
                                                >
                                                    <FaFilePdf />
                                                    <span>Export all tabs as PDF</span>
                                                </button>

                                                <button
                                                    type="button"
                                                    className="flex w-full items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left"
                                                    onClick={() => {
                                                        setShowDataExportMenu(false);
                                                        handleExportAllTabsWord("data");
                                                    }}
                                                >
                                                    <FaFileWord />
                                                    <span>Export all tabs as Word</span>
                                                </button>
                                                <button
                                                type="button"
                                                className="flex w-full items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left"
                                                onClick={() => {
                                                  setShowDataExportMenu(false);
                                                  handleExportAllTabsExcel("data");
                                                }}
                                                >
                                                <FaFileExcel />
                                                <span>Export all tabs as Excel</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Body */}
                        {dataModal.loading ? (
                            <div className="text-center py-10">Loading…</div>
                        ) : (() => {
                            const fctDto = dataModal.content[dataModal.activeTab] || {};
                            const { products: dtoProducts = [], rows: dtoRows = [], productFeaturesMap = {} } = fctDto;

                            return (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full table-auto border-collapse">
                                        <thead>
                                        <tr className="bg-[#25408f] text-white">
                                            <th className="p-2 text-left align-middle">Data Items</th>
                                            {dtoProducts.map((p) => (
                                                <th
                                                    key={p.id}
                                                    className="p-2 text-center align-middle"
                                                    title={p.name}
                                                >
                                                    <div className="whitespace-normal break-words leading-tight">
                                                        {splitTwoWordsPerLine(p.name).map((line, idx) => (
                                                            <div key={idx}>{line}</div>
                                                        ))}
                                                    </div>
                                                </th>
                                            ))}
                                        </tr>
                                        </thead>

                                        <tbody>
                                        {dtoRows.map((r) => (
                                            <tr key={r.featureId} className={`${r.mainHeader ? "bg-gray-200 font-semibold" : ""}`}>
                                                <td className="p-2 align-middle">{r.label}</td>

                                                {dtoProducts.map((p) => {
                                                    const has = (productFeaturesMap[p.id] || []).includes(r.featureId);
                                                    return (
                                                        <td key={p.id} className="p-2 align-middle">
                                                            {!r.mainHeader && (
                                                                <span className="inline-flex w-full items-center justify-center">
                              {has ? <RustyTick /> : <RustyCross />}
                            </span>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            );
                        })()}
                    </DraggableModalBox>
                </div>
            )}

            {/* Functionality Popup (also supports MANY products) */}
            {functionalityModal.visible && (
                <div
                    className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
                    style={{ zIndex: functionalityModalZIndex }}
                    onClick={(e) => {
                        // If clicking on the backdrop (not the modal content), lower z-index
                        if (e.target === e.currentTarget) {
                            setFunctionalityModalZIndex(999);
                        }
                    }}
                >
                    <DraggableModalBox
                        onClose={() => {
                            setShowFuncExportMenu(false);
                            setFunctionalityModal((dm) => ({ ...dm, visible: false }));
                        }}
                        onFocus={() => setFunctionalityModalZIndex(2000)}
                    >
                        <div className="mb-4">
                            {/* Close row */}
                            <div className="flex justify-end mb-2">
                                <button
                                    className="text-2xl font-bold leading-none px-2 hover:text-red-500"
                                    type="button"
                                    onClick={() => {
                                        setShowFuncExportMenu(false);
                                        setFunctionalityModal((dm) => ({ ...dm, visible: false }));
                                    }}
                                >
                                    ×
                                </button>
                            </div>

                            {/* Tabs + product count + 3-dots */}
                            <div className="flex items-center justify-between gap-4">
                                <div className="flex border-b border-gray-200 flex-shrink-0">
                                    {[1, 2, 3].map((tab) => {
                                        const label = tab === 1 ? "Fixed Income" : tab === 2 ? "Equity" : "Commodity";
                                        const isActive = functionalityModal.activeTab === tab;
                                        const accentBg = { backgroundColor: "var(--rusty-orange, #e87722)", color: "#fff" };
                                        return (
                                            <button
                                                key={tab}
                                                onClick={() => setFunctionalityModal((dm) => ({ ...dm, activeTab: tab }))}
                                                className={`px-4 py-2 rounded border -mb-px transition ${
                                                    isActive ? "" : "border-transparent text-gray-500 hover:text-gray-700"
                                                }`}
                                                style={isActive ? accentBg : undefined}
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>

                                <div className="flex items-center gap-3 text-sm text-gray-600 ml-auto">
            <span>
              {(() => {
                  const dto = functionalityModal.content[functionalityModal.activeTab] || {};
                  const count = (dto.products || []).length;
                  return count ? `${count} product${count !== 1 ? "s" : ""}` : "";
              })()}
            </span>

                                    <div className="relative">
                                        <button
                                            type="button"
                                            className="p-1 rounded hover:bg-gray-100"
                                            title="More options"
                                            onClick={() => setShowFuncExportMenu((prev) => !prev)}
                                        >
                                            <FaEllipsisV />
                                        </button>

                                        {showFuncExportMenu && (
                                            <div className="absolute right-0 mt-2 w-44 rounded-md border bg-white shadow-lg z-20 text-sm">
                                                <button
                                                    type="button"
                                                    className="flex w-full items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left"
                                                    onClick={() => {
                                                        setShowFuncExportMenu(false);
                                                        handlePrintAllTabs("functionality");
                                                    }}
                                                >
                                                    <FaPrint />
                                                    <span>Print all tabs</span>
                                                </button>

                                                <button
                                                    type="button"
                                                    className="flex w-full items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left"
                                                    onClick={() => {
                                                        setShowFuncExportMenu(false);
                                                        handleExportAllTabsPdf("functionality");
                                                    }}
                                                >
                                                    <FaFilePdf />
                                                    <span>Export all tabs as PDF</span>
                                                </button>

                                                <button
                                                    type="button"
                                                    className="flex w-full items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left"
                                                    onClick={() => {
                                                        setShowFuncExportMenu(false);
                                                        handleExportAllTabsWord("functionality");
                                                    }}
                                                >
                                                    <FaFileWord />
                                                    <span>Export all tabs as Word</span>
                                                </button>
                                                <button
                                                type="button"
                                                className="flex w-full items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left"
                                                onClick={() => {
                                                  setShowFuncExportMenu(false);
                                                  handleExportAllTabsExcel("functionality");
                                                }}
                                                >
                                                <FaFileExcel />
                                                <span>Export all tabs as Excel</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Body */}
                        {functionalityModal.loading ? (
                            <div className="text-center py-10">Loading…</div>
                        ) : (() => {
                            const fctDto = functionalityModal.content[functionalityModal.activeTab] || {};
                            const { products: dtoProducts = [], rows: dtoRows = [], productFeaturesMap = {} } = fctDto;

                            return (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full table-auto border-collapse">
                                        <thead>
                                        <tr className="bg-[#25408f] text-white">
                                            <th className="p-2 text-left align-middle">Functionality Items</th>
                                            {dtoProducts.map((p) => (
                                                <th
                                                    key={p.id}
                                                    className="p-2 text-center align-middle"
                                                    title={p.name}
                                                >
                                                    <div className="whitespace-normal break-words leading-tight">
                                                        {splitTwoWordsPerLine(p.name).map((line, idx) => (
                                                            <div key={idx}>{line}</div>
                                                        ))}
                                                    </div>
                                                </th>
                                            ))}
                                        </tr>
                                        </thead>

                                        <tbody>
                                        {dtoRows.map((r) => (
                                            <tr key={r.featureId} className={r.mainHeader ? "bg-gray-200 font-semibold" : ""}>
                                                <td className="p-2 text-left align-middle">{r.label}</td>

                                                {dtoProducts.map((p) => {
                                                    const has = (productFeaturesMap[p.id] || []).includes(r.featureId);
                                                    return (
                                                        <td key={p.id} className="p-2 align-middle">
                                                            {!r.mainHeader && (
                                                                <div className="flex items-center justify-center">
                                                                    {has ? <RustyTick /> : <RustyCross />}
                                                                </div>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            );
                        })()}
                    </DraggableModalBox>
                </div>
            )}

        </>
    );
}
